import React from "react";
import { Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { DATA_GRID_FONT_SIZE } from "../../../components/ui/dataGridStyles";
import { mapTransportistasAOpciones } from "../utils/transportistas";
import { SIN_TRANSPORTISTA_ASIGNADO } from "../constants";

// Fallback de texto para el transportista cuando el código no coincide con
// ningún valueOption (TASK-012, criterio 5). Reutilizado por valueFormatter
// y renderCell para no duplicar el literal.
const SIN_ASIGNAR_LABEL = "Sin asignar";

/**
 * Definiciones de columnas para DataGrid de rangos de precios
 */

/**
 * Genera las columnas del DataGrid de rangos de precios
 * @param {Array<{CODIGO: string, NOMBRE: string}>} transportistas - Transportistas tal como los expone useRangos (sin mapear)
 * @param {Object} handlers - Callbacks de acciones de fila
 * @param {Function} handlers.onEditar - Callback al pulsar editar, recibe la fila completa
 * @param {Function} handlers.onEliminar - Callback al pulsar eliminar, recibe la fila completa
 * @returns {Array} Definiciones de columnas para el DataGrid
 */
export const generateRangoColumns = (transportistas, { onEditar, onEliminar }) => {
    // Se calcula una sola vez y se reutiliza en valueOptions y valueFormatter,
    // para que ambos consulten exactamente el mismo listado de opciones
    const opcionesTransportista = mapTransportistasAOpciones(transportistas);

    return [
        {
            field: "id",
            headerName: "ID",
            flex: 0.3,
            editable: false,
        },
        {
            field: "isla",
            headerName: "Isla",
            flex: 1,
            type: "text",
        },
        {
            field: "idAgencia",
            headerName: "Transportista",
            type: "singleSelect",
            flex: 2,
            editable: false,
            valueOptions: opcionesTransportista,
            // Ver comentario de SIN_TRANSPORTISTA_ASIGNADO: fallback conservado del legado
            valueGetter: (params) => params.value || SIN_TRANSPORTISTA_ASIGNADO,
            // TASK-012 (decisión del usuario): unifica el fallback visual con
            // RangosMobileList. Sin este valueFormatter, gridSingleSelectColDef
            // pinta cadena vacía cuando el código no coincide con ningún
            // valueOption (ver node_modules/@mui/x-data-grid/colDef/
            // gridSingleSelectColDef.js:50, `return valueOption ? ... : ''`)
            valueFormatter: (params) => {
                const opcion = opcionesTransportista.find((o) => o.value === params.value);
                return opcion ? opcion.label : SIN_ASIGNAR_LABEL;
            },
            // CLAUDE.md, "Estados Especiales": "Sin asignar" en gris (#6c757d),
            // nunca en rojo/negro. Usa formattedValue (salida de valueFormatter),
            // no value (el código crudo/con fallback "000")
            renderCell: (params) => (
                <Typography
                    sx={{
                        fontSize: DATA_GRID_FONT_SIZE.CELDA,
                        color: params.formattedValue === SIN_ASIGNAR_LABEL ? "#6c757d" : "#495057",
                    }}
                >
                    {params.formattedValue}
                </Typography>
            ),
        },
        {
            field: "rango",
            headerName: "Rango",
            flex: 2.7,
            type: "text",
        },
        {
            field: "precio",
            headerName: "Precio bulto",
            flex: 1,
            type: "text",
        },
        {
            field: "palet",
            headerName: "Precio palet",
            flex: 1,
            type: "text",
        },
        {
            field: "actions",
            type: "actions",
            headerName: "",
            flex: 0.7,
            sortable: false,
            filterable: false,
            cellClassName: "actions",
            // DECISION: los callbacks reciben la fila completa, no solo el id, para que la
            // pagina pueda pasar el mismo handler a esta tabla y a RangosMobileList sin
            // envoltorios. Ademas tanto el modal de edicion como el de confirmacion de
            // borrado necesitan los datos de la fila, no unicamente su identificador
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key="editar"
                    icon={<EditIcon />}
                    label="Editar rango"
                    onClick={() => onEditar(row)}
                    color="inherit"
                />,
                <GridActionsCellItem
                    key="eliminar"
                    icon={<DeleteIcon />}
                    label="Eliminar rango"
                    onClick={() => onEliminar(row)}
                    color="inherit"
                />,
            ],
        },
    ];
};
